import { useState } from 'react';
import type { LeadCapture } from '../../types/generator-config';
import type { LeadContact } from '../../hooks/useSubmitLead';

interface LeadCaptureFormProps {
    config: LeadCapture;
    submitting: boolean;
    error: string | null;
    onSubmit: (contact: LeadContact) => void;
    /** Ignoré si true (mode preview éditeur) : on simule juste le submit */
    previewMode?: boolean;
}

interface FormState {
    name: string;
    email: string;
    phone: string;
    company: string;
    message: string;
    consent: boolean;
}

const EMPTY: FormState = { name: '', email: '', phone: '', company: '', message: '', consent: false };

export function LeadCaptureForm({ config, submitting, error, onSubmit, previewMode }: LeadCaptureFormProps) {
    const [state, setState] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const setField = <K extends keyof FormState>(k: K, v: FormState[K]) => {
        setState((prev) => ({ ...prev, [k]: v }));
        if (errors[k as string]) {
            const next = { ...errors };
            delete next[k as string];
            setErrors(next);
        }
    };

    const validate = (): boolean => {
        const next: Record<string, string> = {};
        if (config.fields.name.enabled && config.fields.name.required && !state.name.trim()) {
            next.name = 'Nom requis';
        }
        if (config.fields.email.enabled) {
            if (config.fields.email.required && !state.email.trim()) {
                next.email = 'Email requis';
            } else if (state.email.trim() && !/^\S+@\S+\.\S+$/.test(state.email.trim())) {
                next.email = 'Email invalide';
            }
        }
        if (config.fields.phone.enabled && config.fields.phone.required && !state.phone.trim()) {
            next.phone = 'Téléphone requis';
        }
        if (config.fields.company.enabled && config.fields.company.required && !state.company.trim()) {
            next.company = 'Entreprise requise';
        }
        if (config.fields.message.enabled && config.fields.message.required && !state.message.trim()) {
            next.message = 'Message requis';
        }
        if (config.consentText && !state.consent) {
            next.consent = 'Vous devez accepter pour continuer';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        onSubmit({
            name: state.name.trim(),
            email: state.email.trim(),
            phone: state.phone.trim(),
            company: state.company.trim() || undefined,
            message: state.message.trim() || undefined,
        });
    };

    return (
        <form className="lead-form" onSubmit={handleSubmit} noValidate>
            <div className="lead-form-head">
                <h4>{config.title}</h4>
                {config.description && <p>{config.description}</p>}
            </div>

            <div className="lead-form-grid">
                {config.fields.name.enabled && (
                    <label className={`lead-field${errors.name ? ' has-error' : ''}`}>
                        <span className="lead-field-label">
                            Nom{config.fields.name.required ? ' *' : ''}
                        </span>
                        <input
                            type="text"
                            value={state.name}
                            onChange={(e) => setField('name', e.target.value)}
                            autoComplete="name"
                            disabled={submitting}
                        />
                        {errors.name && <span className="lead-field-err">{errors.name}</span>}
                    </label>
                )}

                {config.fields.email.enabled && (
                    <label className={`lead-field${errors.email ? ' has-error' : ''}`}>
                        <span className="lead-field-label">
                            Email{config.fields.email.required ? ' *' : ''}
                        </span>
                        <input
                            type="email"
                            value={state.email}
                            onChange={(e) => setField('email', e.target.value)}
                            autoComplete="email"
                            disabled={submitting}
                        />
                        {errors.email && <span className="lead-field-err">{errors.email}</span>}
                    </label>
                )}

                {config.fields.phone.enabled && (
                    <label className={`lead-field${errors.phone ? ' has-error' : ''}`}>
                        <span className="lead-field-label">
                            Téléphone{config.fields.phone.required ? ' *' : ''}
                        </span>
                        <input
                            type="tel"
                            value={state.phone}
                            onChange={(e) => setField('phone', e.target.value)}
                            autoComplete="tel"
                            disabled={submitting}
                        />
                        {errors.phone && <span className="lead-field-err">{errors.phone}</span>}
                    </label>
                )}

                {config.fields.company.enabled && (
                    <label className={`lead-field${errors.company ? ' has-error' : ''}`}>
                        <span className="lead-field-label">
                            Entreprise{config.fields.company.required ? ' *' : ''}
                        </span>
                        <input
                            type="text"
                            value={state.company}
                            onChange={(e) => setField('company', e.target.value)}
                            autoComplete="organization"
                            disabled={submitting}
                        />
                        {errors.company && <span className="lead-field-err">{errors.company}</span>}
                    </label>
                )}
            </div>

            {config.fields.message.enabled && (
                <label className={`lead-field lead-field-full${errors.message ? ' has-error' : ''}`}>
                    <span className="lead-field-label">
                        Message{config.fields.message.required ? ' *' : ''}
                    </span>
                    <textarea
                        value={state.message}
                        onChange={(e) => setField('message', e.target.value)}
                        rows={3}
                        disabled={submitting}
                        placeholder="Précisez votre besoin, vos contraintes, vos délais…"
                    />
                    {errors.message && <span className="lead-field-err">{errors.message}</span>}
                </label>
            )}

            {config.consentText && (
                <label className={`lead-consent${errors.consent ? ' has-error' : ''}`}>
                    <input
                        type="checkbox"
                        checked={state.consent}
                        onChange={(e) => setField('consent', e.target.checked)}
                        disabled={submitting}
                    />
                    <span>{config.consentText}</span>
                    {errors.consent && <span className="lead-field-err">{errors.consent}</span>}
                </label>
            )}

            {error && <div className="lead-form-error" role="alert">{error}</div>}
            {previewMode && (
                <div className="lead-form-preview-note">
                    Mode aperçu : le formulaire ne sera pas réellement envoyé.
                </div>
            )}

            <button type="submit" className="preview-btn-primary" disabled={submitting}>
                {submitting ? 'Envoi…' : config.submitLabel}
            </button>
        </form>
    );
}
